<template>
    <div>
        <div class="d-flex flex-columns">
            <AutocompleteDisplay
                :collection="collection"
                item-text="titleWithLocation"
                item-value="id"
                :selected="locationSelected"
                :display-value="locationSelected?.titleWithLocation"
                :placeholder="placeholder"
                display-hide-details="auto"
                query-param="simpleQuery"
                clearable
                :tabindex="tabindex"
                v-bind="$attrs"
                :sorter="sorter"
                @selected="selected"
                v-on="$listeners"
            >
                <template #item="{ item, on, attrs }">
                    <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
                </template>
            </AutocompleteDisplay>
            <IconBtn
                v-if="showEdit && locationId"
                :disabled="form?.disabled"
                margin-class="ma-0"
                color="transparent"
                icon-cls="pencil"
                icon-color="black"
                :tooltip="$t('edit_location')"
                :loading="loading"
                @click="editLocation"
            />
            <IconBtn
                v-if="showNew"
                margin-class="ma-0"
                :disabled="form?.disabled"
                color="transparent"
                icon-cls="new"
                icon-color="black"
                :tooltip="$t('create_location')"
                @click="newLocation"
            />
            <EditDialog
                v-if="showDlg"
                v-model="showDlg"
                :location="locationToEdit"
                :organizer-id="organizerId"
                :writable="showDlgWritable"
                @address-changed="locationChanged"
                @location-changed="locationChanged"
                @location-added="locationChanged"
            />
            <AddressNewRecordDialog
                v-if="showNewRecordDialog"
                :value="showNewRecordDialog"
                @input="showNewRecordDialog = $event"
                @new-address="showNewAddress"
            />
        </div>
        <AddressIconBar v-if="showIconBar && locationSelected" :item="locationSelected" size="12" />
    </div>
</template>

<script>
import AddressComboListItem from '@/components/address/AddressComboListItem.vue';
import EditDialog from '@/components/location/EditDialog.vue';
import { instance as api } from '@/helpers/api';
import { userIsStaff } from '@/helpers/auth';
import { LocationCollection } from '@/models/Location';
import { mapActions } from 'vuex';
import AddressNewRecordDialog from '@/components/address/NewRecordDialog.vue';
import AddressIconBar from '../address/AddressIconBar.vue';

export default {
    name: 'LocationSelectField',
    components: {
        AddressComboListItem,
        EditDialog,
        AddressNewRecordDialog,
        AddressIconBar,
    },

    // This component need to be part of a form component: We want to check
    // for form props like validity and disabled state:
    inject: {
        form: {
            from: 'form',
            default: null,
        },
    },

    props: {
        placeholder: {
            type: String,
            default: '',
        },
        locationId: {
            type: Number,
            default: null,
        },
        organizerId: {
            type: Number,
            default: null,
        },
        showNew: {
            type: Boolean,
            default: true,
        },
        showEdit: {
            type: Boolean,
            default: true,
        },
        showAllLocations: {
            type: Boolean,
            default: true,
        },
        showIconBar: {
            type: Boolean,
            default: true,
        },
        cityFilter: {
            type: String,
            default: '',
        },
        sorter: {
            type: [Object, Array],
            default: null,
        },
        tabindex: {
            type: String,
            default: null,
        },
    },

    data() {
        const locationCollection = new LocationCollection();
        if (!userIsStaff() && this.showAllLocations) {
            locationCollection.addExtraParam('showAllLocations', true);
        }
        window.locations = locationCollection;
        return {
            collection: locationCollection,
            locationToEdit: null,
            locationSelected: null,
            showDlg: false,
            showDlgWritable: false,
            loading: false,
            showNewRecordDialog: false,
        };
    },

    watch: {
        locationId: {
            handler(newValue) {
                this.locationIdChanged(newValue);
            },
            immediate: true,
        },
        locationSelected(newVal) {
            // we add the location to the autocomplete store, so that we can display it
            // in the text field when clicked into:
            if (newVal) {
                if (!this.collection.find((entry) => entry.id === newVal.id)) {
                    this.collection.add(newVal);
                }
            }
        },
        cityFilter: {
            handler(newValue, oldValue) {
                if (newValue && newValue !== oldValue) {
                    const enhancedFilters = { ort_like: newValue };
                    this.collection.addExtraParam('enhancedFilters', JSON.stringify(enhancedFilters));
                } else {
                    this.collection.removeExtraParam('enhancedFilters');
                }
                this.collection.query();
            },
        },
    },

    methods: {
        ...mapActions(['runAsync']),

        locationIdChanged(id) {
            if (id !== this.locationSelected?.getId()) {
                this.locationSelected = null;
            }
            this.loadLocationSelected();
        },

        async loadLocationSelected(force = false) {
            if (!this.locationSelected || force) {
                this.locationSelected = await this.loadLocationForId(this.locationId);
            }
            return this.locationSelected;
        },

        async loadLocationForId(id) {
            if (!id) {
                return null;
            }
            const collection = new LocationCollection();
            await collection.query({ id, showAllLocations: true });
            return collection.first();
        },

        selected(location) {
            this.locationSelected = location;
        },

        newLocation() {
            this.locationToEdit = null;
            this.showNewRecordDialog = true;
        },

        showNewAddress(address, location) {
            this.locationSelected = location;
            this.editLocation();
        },

        async editLocation() {
            this.loading = true;
            const loc = await this.loadLocationSelected();
            const isWritable = await this.loadIsWritable(loc);
            this.loading = false;
            this.locationToEdit = this.locationSelected;
            this.showDlgWritable = isWritable;
            this.showDlg = true;
        },

        async locationChanged(data) {
            const location = await this.loadLocationForId(data?.location?.getId());
            this.locationSelected = location;
            this.$emit('selected', location);
        },

        async loadIsWritable(location) {
            if (location) {
                const res = await api().get('/Location/isWritable', {
                    params: {
                        id: location.id,
                    },
                });
                return res?.data?.writable === true;
            }
            return false;
        },
    },
};
</script>

<style lang="scss" scoped></style>
